import React from 'react';

import { Label } from 'cpool-components/general/Label';
import { SegmentedControl } from 'cpool-components/input/SegmentedControl';
import type { SegmentedControlOption } from 'cpool-components/input/SegmentedControl/types';
import type { EMPLOYMENT_LIMITATION } from 'cpool-constants';
import { EMPLOYMENT_LIMITATIONS } from 'cpool-constants';
import type { FieldValues } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TitleDateInput from './general/TitleDateInput.tsx';
import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useApplicantInfoContext } from '~/features/leadDetails/context/ApplicantInfoContext.tsx';

const NOT_SPECIFIED = 'NOT_SPECIFIED';

type Props = {
    limitationPath: string;
    limitationTimePath: string;
    disabled?: boolean;
};

const TimeLimit: React.FC<Props> = ({ disabled, limitationPath, limitationTimePath }) => {
    const { t } = useTranslation();
    const { applicantId } = useApplicantInfoContext();
    const { control } = useFormContext();
    const value = useWatch({
        name: limitationPath,
    });

    const employmentPath = 'CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT';

    const options: SegmentedControlOption<EMPLOYMENT_LIMITATION | typeof NOT_SPECIFIED>[] = [
        { label: t('VALUES.YES'), value: EMPLOYMENT_LIMITATIONS.BEFRISTET },
        { label: t('VALUES.NO'), value: EMPLOYMENT_LIMITATIONS.UNBEFRISTET },
        { label: t('VALUES.NOT_SPECIFIED'), value: NOT_SPECIFIED },
    ];

    return (
        <>
            <FormField<FieldValues, EMPLOYMENT_LIMITATION | null> control={control} name={limitationPath}>
                {({ error, onChange, value: fieldValue }) => (
                    <div className="flex items-start gap-2.5">
                        <Label className="h-8 w-[175px] shrink-0 justify-start">
                            {t(`${employmentPath}.LIMITATION.TITLE`)}
                        </Label>
                        <SegmentedControl<EMPLOYMENT_LIMITATION | typeof NOT_SPECIFIED>
                            id={`${limitationPath}-${applicantId}`}
                            ariaLabel={t(`${employmentPath}.LIMITATION.TITLE`)}
                            options={options}
                            value={fieldValue ?? NOT_SPECIFIED}
                            onChange={(newValue) => onChange(newValue === NOT_SPECIFIED ? null : newValue)}
                            disabled={disabled}
                            error={error}
                            wide
                        />
                    </div>
                )}
            </FormField>
            {value === EMPLOYMENT_LIMITATIONS.BEFRISTET && (
                <TitleDateInput
                    title={t(`${employmentPath}.LIMITATION.LIMITATION_TIME`)}
                    name={limitationTimePath}
                    disabled={disabled}
                />
            )}
        </>
    );
};

export default TimeLimit;
